"use client";

import { HeroBackgroundVideo } from "./HeroBackgroundVideo";
import { HeroBody } from "@/components/hero/HeroBody";
import { resolveApiAssetUrl } from "@/lib/backend";
import type { HeroContent } from "@/components/hero/types";

export type HeroData = {
  title?: string;
  subtitle?: string;
  badge_text?: string;
  cta_primary_label?: string;
  cta_primary_url?: string;
  cta_secondary_label?: string;
  cta_secondary_url?: string;
  bg_image_url?: string;
  overlay_opacity?: number | string;
  /** Animated hero theme when background is image and no custom bg image. */
  theme?: string;
  background_type?: "image" | "video" | string;
  video_url?: string;
  poster_url?: string;
  provider?: string;
};

function parseOpacity(value: number | string | undefined, fallback: number): number {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const n = parseFloat(value);
    if (!Number.isNaN(n)) return n;
  }
  return fallback;
}

function heroContent(data: HeroData): HeroContent {
  return {
    badge: data.badge_text,
    title: data.title ?? "",
    subtitle: data.subtitle,
    primaryCta:
      data.cta_primary_label && data.cta_primary_url
        ? { label: data.cta_primary_label, href: data.cta_primary_url }
        : undefined,
    secondaryCta:
      data.cta_secondary_label && data.cta_secondary_url
        ? { label: data.cta_secondary_label, href: data.cta_secondary_url }
        : undefined,
  };
}

export function HeroSection({ data }: { data: HeroData }) {
  const bgType = String(data.background_type || "image").toLowerCase();
  const isVideo = bgType === "video" && Boolean(data.video_url?.trim());
  const bgUrl = !isVideo ? resolveApiAssetUrl(data.bg_image_url) : null;
  const opacity = parseOpacity(data.overlay_opacity, isVideo ? 0.72 : 0.85);

  const gradientOverlay = `linear-gradient(145deg, rgba(0,34,68,${opacity}) 0%, rgba(0,51,102,${Math.max(0, opacity - 0.06)}) 42%, rgba(0,79,128,${Math.max(0, opacity - 0.1)}) 100%)`;

  return (
    <section className="hero-section relative overflow-hidden px-4 py-16 text-white md:py-28">
      {isVideo ? (
        <>
          <HeroBackgroundVideo
            videoUrl={data.video_url!}
            providerHint={data.provider}
            posterUrl={data.poster_url}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ background: gradientOverlay }}
          />
        </>
      ) : bgUrl ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `${gradientOverlay}, url(${JSON.stringify(bgUrl)})`,
          }}
        />
      ) : (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[var(--primary-darker)] via-[var(--primary)] to-[var(--primary-dark)]"
        />
      )}

      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        aria-hidden
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 80% 70% at 50% 20%, black 15%, transparent 65%)",
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-1 flex-col items-center justify-center py-8">
        <HeroBody content={heroContent(data)} />
      </div>
    </section>
  );
}
